import axios from 'axios'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { useEffect, useState } from 'react'

import type { ApiResponse } from '../../types/api'
import type { WithdrawTypes } from '../../types/wallet-management'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import Icon from '../icon'

export interface AmountInputProps {
  amount: number
  setAmount: Dispatch<SetStateAction<number>>
  page: string
  type?: WithdrawTypes
  tooltip?: ReactNode
  error?: string
}

const Button = ({
  children,
  onClick,
}: {
  children: ReactNode
  onClick: () => void
}) => (
  <button
    className="flx-ctr w-[45px] lg:w-[55px] h-[45px] lg:h-[55px] text-3xl text-txt bg-tertiary rounded cursor-pointer"
    onClick={onClick}>
    {children}
  </button>
)

const AmountInput = ({
  amount,
  tooltip,
  setAmount,
  page,
  type,
  error,
}: AmountInputProps) => {
  const [conversion, setConversion] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    const fetchConversion = async () => {
      try {
        const response = await api.post<ApiResponse<number>>(
          '/request.php',
          {
            type: 'convert_currency',
          },
          { signal },
        )
        const { status, info } = response.data
        if (status === 1) {
          setConversion(info)
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      }
    }
    fetchConversion()

    return () => {
      controller.abort()
    }
  }, [])

  return (
    <>
      <p className="flx-str-ctr text-base lg:text-lg text-txt mb-3 lg:mb-4">
        {page} {tooltip && tooltip}
      </p>
      <div
        className={classNames(
          'flx-ctr lg:mb-[30px]',
          type === 'usdt' || error ? 'mb-3' : 'mb-5',
        )}>
        <Button
          onClick={() =>
            setAmount(prev => (prev - 50 >= 0 ? prev - 50 : prev))
          }>
          <Icon id="minus" width="16" className="fill-txt lg:w-[18px]" />
        </Button>
        <input
          type="number"
          inputMode="numeric"
          value={amount}
          onChange={e => setAmount(parseFloat(e.target.value))}
          className="flex-1 h-[45px] lg:h-[55px] min-w-0 mx-[15px] text-lg lg:text-[26px] text-txt text-center bg-tertiary font-Avenir-Light rounded "
        />
        <Button onClick={() => setAmount(prev => prev + 50)}>
          <Icon id="plus" width="16" className="fill-txt lg:w-[18px]" />
        </Button>
      </div>
      {type === 'usdt' && (
        <p className="pl-[60px] lg:pl-[75px] mb-5 lg:mb-[30px] text-xs lg:text-base text-txt font-Avenir-Light">
          <span className="font-Syncopate-Bold">USDT = </span>
          {(amount * conversion).toFixed(2)}
        </p>
      )}
      {error && (
        <p className="mb-5 lg:mb-[30px] text-sm text-highlight lg:text-base font-Avenir-Light">
          {error}
        </p>
      )}
    </>
  )
}

export default AmountInput
